import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { createCgsPdfBlobUrl } from "../../../utils/createPdf";
import "./cgsResults.css";

const toNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSeverityTone = (index, totalRanges) => {
  if (index < 0) return "neutral";
  if (totalRanges <= 2) return index === 0 ? "green" : "red";
  if (totalRanges === 3) {
    if (index === 0) return "green";
    if (index === totalRanges - 1) return "red";
    return "yellow";
  }

  if (index === 0) return "green";
  if (index === totalRanges - 1) return "red";
  if (index === totalRanges - 2) return "orange";
  return "yellow";
};

const getToneColorVar = (tone) => {
  switch (tone) {
    case "green":
      return "var(--cgs-tone-green)";
    case "yellow":
      return "var(--cgs-tone-yellow)";
    case "orange":
      return "var(--cgs-tone-orange)";
    case "red":
      return "var(--cgs-tone-red)";
    default:
      return "var(--cgs-tone-neutral)";
  }
};

const getToneSoftVar = (tone) => {
  switch (tone) {
    case "green":
      return "var(--cgs-tone-green-soft)";
    case "yellow":
      return "var(--cgs-tone-yellow-soft)";
    case "orange":
      return "var(--cgs-tone-orange-soft)";
    case "red":
      return "var(--cgs-tone-red-soft)";
    default:
      return "var(--cgs-tone-neutral-soft)";
  }
};

const getHighlightColor = (index, totalRanges) => {
  const tone = getSeverityTone(index, totalRanges);
  return getToneColorVar(tone);
};

const isTotalInRange = (total, range) => {
  if (typeof total !== "number") return false;

  const minOk = range.minInclusive ? total >= range.min : total > range.min;
  const maxOk = typeof range.max === "number" ? total <= range.max : true;

  return minOk && maxOk;
};

const getSelectedRangeIndex = (total, ranges, fallbackIndex) => {
  const byTotal = ranges.findIndex((range) => isTotalInRange(total, range));
  if (byTotal >= 0) return byTotal;

  const parsedFallbackIndex = Number(fallbackIndex);
  if (
    Number.isInteger(parsedFallbackIndex) &&
    parsedFallbackIndex >= 0 &&
    parsedFallbackIndex < ranges.length
  ) {
    return parsedFallbackIndex;
  }

  return -1;
};

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const MODULE_SCORE_GUIDANCE = {
  bai: {
    levels: [
      {
        range: "0-21",
        level: "Muy baja",
        meaning:
          "La persona experimenta síntomas de ansiedad mínimos o ausentes. Su sistema nervioso responde de manera equilibrada ante situaciones cotidianas. No representa interferencia en su funcionamiento diario.",
        recommendations: [
          "Mantener hábitos de autocuidado: actividad física, sueño regular y alimentación balanceada.",
          "Continuar con estrategias de manejo del estrés que ya funcionan (meditación, tiempo en naturaleza, hobbies).",
          "Reevaluación preventiva cada 6 meses."
        ]
      },
      {
        range: "22-35",
        level: "Moderada",
        meaning:
          "Presencia notable de síntomas físicos y cognitivos de ansiedad (tensión muscular, preocupación frecuente, dificultad para relajarse). Puede comenzar a interferir en el rendimiento laboral, las relaciones y la calidad del sueño.",
        recommendations: [
          "Iniciar acompañamiento psicológico con enfoque en Terapia Cognitivo-Conductual (TCC).",
          "Incorporar técnicas de respiración diafragmática y mindfulness de forma regular.",
          "Revisar carga laboral y factores de estrés identificables en el entorno de trabajo.",
          "Evaluar si el patrón persiste más de 2 semanas para derivar a especialista."
        ]
      },
      {
        range: ">=36",
        level: "Severa",
        meaning:
          "Ansiedad intensa que afecta significativamente el funcionamiento global. Pueden presentarse crisis de ansiedad, evitación de situaciones, síntomas somáticos (palpitaciones, sudoración, mareos) y deterioro en relaciones interpersonales y desempeño laboral.",
        recommendations: [
          "Derivación inmediata a psicólogo y/o psiquiatra para evaluación clínica completa.",
          "Considerar evaluación médica para descartar causas orgánicas.",
          "Implementar plan de atención en crisis dentro del programa de salud mental empresarial.",
          "Ajuste de responsabilidades laborales mientras dure el tratamiento."
        ]
      }
    ]
  },
  psqi: {
    levels: [
      {
        range: "0-4",
        level: "Buena calidad",
        meaning:
          "El sueño es reparador y suficiente. La persona concilia el sueño con facilidad, duerme el tiempo adecuado y se despierta sintiéndose descansada. No hay interferencia en la vida diaria por problemas relacionados con el descanso.",
        recommendations: [
          "Preservar rutinas de sueño consistentes (horario regular de acostarse y levantarse).",
          "Evitar pantallas 30–60 min antes de dormir como medida preventiva.",
          "Incluir espacio físico de descanso dentro de la jornada laboral."
        ]
      },
      {
        range: ">5",
        level: "Alteración",
        meaning:
          "Existe una alteración clínicamente significativa del sueño. Puede manifestarse como dificultad para conciliar o mantener el sueño, despertares nocturnos frecuentes, sueño no reparador o somnolencia diurna excesiva. Impacta directamente en el estado de ánimo, la concentración y la productividad.",
        recommendations: [
          "Aplicar protocolo de higiene del sueño: ambiente oscuro, fresco y sin ruido; eliminar cafeína después de las 2 pm.",
          "Explorar posible presencia de insomnio crónico, apnea del sueño u otros trastornos con médico o psicólogo.",
          "Incorporar técnicas de relajación progresiva o meditación guiada antes de dormir.",
          "Revisar si el trabajo en horarios atípicos, el estrés laboral o el uso excesivo de dispositivos están contribuyendo al problema."
        ]
      }
    ]
  },
  pss: {
    levels: [
      {
        range: "0-19",
        level: "Bajo – Moderado bajo",
        meaning:
          "La persona percibe que tiene control sobre su vida y sus demandas cotidianas. Los niveles de estrés son manejables y no representan una amenaza para su bienestar. Cuenta con recursos de afrontamiento funcionales.",
        recommendations: [
          "Fortalecer las estrategias de afrontamiento que ya usa (red de apoyo, organización, ejercicio).",
          "Participar en actividades de bienestar preventivo: talleres de resiliencia, pausas activas.",
          "Reevaluación semestral para detectar cambios ante nuevas exigencias laborales."
        ]
      },
      {
        range: "20-25",
        level: "Moderado",
        meaning:
          "Se percibe un nivel de estrés que empieza a ser demandante. La persona puede sentir que sus recursos no son suficientes para manejar sus responsabilidades. Pueden aparecer señales como irritabilidad, fatiga, dificultad para desconectarse del trabajo o tensión física frecuente.",
        recommendations: [
          "Iniciar sesiones de coaching de bienestar o psicoeducación sobre manejo del estrés.",
          "Enseñar técnicas de gestión del tiempo y priorización de tareas.",
          "Revisar la carga de trabajo con el área de Recursos Humanos para identificar factores organizacionales.",
          "Incorporar práctica regular de mindfulness o actividad física (mínimo 3 veces por semana)."
        ]
      },
      {
        range: ">25",
        level: "Elevado",
        meaning:
          "El estrés percibido es alto y crónico. La persona siente que los eventos de su vida están fuera de su control y que no puede manejar las demandas que enfrenta. Esto aumenta el riesgo de burnout, enfermedades físicas, trastornos del sueño y deterioro en las relaciones interpersonales.",
        recommendations: [
          "Iniciar psicoterapia individual de forma inmediata (TCC, ACT o terapia de reducción de estrés basada en mindfulness – MBSR).",
          "Evaluación de factores de riesgo psicosocial en el entorno laboral (NOM-035).",
          "Revisar la posibilidad de ajuste de responsabilidades, licencia temporal o apoyo adicional.",
          "Monitoreo mensual de indicadores de salud mental hasta estabilización."
        ]
      }
    ]
  },
  bdi: {
    levels: [
      {
        range: "0-13",
        level: "Mínima",
        meaning:
          "Ausencia o presencia mínima de síntomas depresivos. El estado de ánimo es estable, la persona mantiene motivación, disfruta de actividades y tiene una percepción positiva de sí misma y del futuro.",
        recommendations: [
          "Mantener redes de apoyo social activas dentro y fuera del trabajo.",
          "Participar en actividades de bienestar emocional preventivo (talleres, cafés con sentido, sesiones de mindfulness).",
          "Seguimiento anual de bienestar emocional."
        ]
      },
      {
        range: "14-19",
        level: "Leve",
        meaning:
          "Presencia de síntomas depresivos leves que pueden incluir tristeza ocasional, menor energía, ligera pérdida de interés en actividades habituales o pensamientos pesimistas esporádicos. No impide el funcionamiento, pero puede afectar la calidad de vida si no se atiende.",
        recommendations: [
          "Iniciar psicoeducación sobre bienestar emocional y reconocimiento de señales tempranas.",
          "Motivar la actividad física regular, que tiene efecto comprobado sobre el estado de ánimo.",
          "Ofrecer espacio de escucha activa o sesiones de apoyo psicológico breve.",
          "Monitoreo cada 4–6 semanas para detectar evolución."
        ]
      },
      {
        range: "20-28",
        level: "Moderada",
        meaning:
          "Los síntomas depresivos son frecuentes e interfieren notablemente en el funcionamiento diario. Pueden presentarse: tristeza persistente, fatiga, dificultad para concentrarse, sentimientos de culpa o inutilidad, cambios en apetito o sueño. El rendimiento laboral y las relaciones personales se ven afectadas.",
        recommendations: [
          "Derivación a psicólogo clínico para inicio de psicoterapia estructurada (TCC o terapia interpersonal).",
          "Evaluación psiquiátrica para determinar si se requiere apoyo farmacológico.",
          "Comunicar al área de Recursos Humanos para ajuste temporal de carga laboral.",
          "Seguimiento quincenal hasta mejoría sostenida."
        ]
      },
      {
        range: "29-63",
        level: "Grave",
        meaning:
          "Depresión severa con impacto profundo en todas las áreas de vida. Síntomas intensos y persistentes: incapacidad para realizar actividades cotidianas, sentimientos de desesperanza, pensamientos negativos recurrentes sobre sí mismo o el futuro. Posible riesgo de ideación suicida que debe ser evaluado de inmediato.",
        recommendations: [
          "Atención psiquiátrica urgente y evaluación de riesgo de manera inmediata.",
          "Activar protocolo de contención emocional de la empresa si existe.",
          "No dejar a la persona sin red de apoyo; involucrar a contacto de confianza o familiar cercano.",
          "Baja laboral temporal si el médico tratante lo considera necesario.",
          "Seguimiento intensivo semanal hasta estabilización clínica."
        ]
      }
    ]
  }
};

const MODULE_RECOMMENDATIONS = {
  green: [
    "Mantén los hábitos que hoy te están ayudando y monitorea cambios en tu rutina.",
    "Si notas variaciones en sueño, ánimo o estrés, repite la evaluación para comparar evolución."
  ],
  yellow: [
    "Observa qué situaciones disparan más malestar y registra patrones (horarios, carga, descanso).",
    "Pequeños ajustes en sueño, pausas y actividad física pueden ayudar antes de que aumente la carga."
  ],
  orange: [
    "Conviene dar seguimiento cercano a este resultado y apoyarte en una red de confianza.",
    "Si el malestar interfiere con tu rutina, busca orientación profesional para una valoración personalizada."
  ],
  red: [
    "Este resultado sugiere una afectación importante y vale la pena atenderlo cuanto antes.",
    "Te recomendamos buscar apoyo profesional para una interpretación clínica y un plan de acompañamiento."
  ],
  neutral: [
    "No se pudo identificar la categoría del resultado con la información recibida.",
    "Intenta repetir la consulta o comparte el resultado con el equipo para validarlo."
  ]
};

const isTotalInTextRange = (total, rangeText) => {
  if (typeof total !== "number" || typeof rangeText !== "string") return false;

  const normalizedRange = rangeText.replace(/\s+/g, "");
  const numberPattern = "(-?\\d+(?:\\.\\d+)?)";

  const betweenMatch = normalizedRange.match(new RegExp(`^${numberPattern}-${numberPattern}$`));
  if (betweenMatch) {
    const min = Number(betweenMatch[1]);
    const max = Number(betweenMatch[2]);
    return total >= min && total <= max;
  }

  const gteMatch = normalizedRange.match(new RegExp(`^>=${numberPattern}$`));
  if (gteMatch) return total >= Number(gteMatch[1]);

  const lteMatch = normalizedRange.match(new RegExp(`^<=${numberPattern}$`));
  if (lteMatch) return total <= Number(lteMatch[1]);

  const gtMatch = normalizedRange.match(new RegExp(`^>${numberPattern}$`));
  if (gtMatch) return total > Number(gtMatch[1]);

  const ltMatch = normalizedRange.match(new RegExp(`^<${numberPattern}$`));
  if (ltMatch) return total < Number(ltMatch[1]);

  const exact = Number(normalizedRange);
  return Number.isFinite(exact) ? total === exact : false;
};

const getModuleGuidanceLevels = (moduleKey) => {
  const levels = MODULE_SCORE_GUIDANCE[moduleKey]?.levels;
  return Array.isArray(levels) ? levels : [];
};

const getModuleGuidanceLevel = (moduleKey, total, fallbackIndex) => {
  const levels = getModuleGuidanceLevels(moduleKey);
  if (!levels.length) return null;

  const byTotal = levels.find((level) => isTotalInTextRange(total, level?.range));
  if (byTotal) return byTotal;

  const parsedFallbackIndex = Number(fallbackIndex);
  if (
    Number.isInteger(parsedFallbackIndex) &&
    parsedFallbackIndex >= 0 &&
    parsedFallbackIndex < levels.length
  ) {
    return levels[parsedFallbackIndex];
  }

  return null;
};

const MODULES = [
  {
    key: "bai",
    shortTitle: "Ansiedad",
    title: "Cuestionario de Ansiedad de Beck",
    description:
      "Valorar la severidad de los síntomas de ansiedad, y discriminar de manera fiable entre ansiedad y depresión. Instrucciones: indique para cada uno de los siguientes síntomas el grado de aplicación en que se ha visto afectado por cada uno de ellos durante la última semana y en el momento actual.",
    scoreMax: 63,
    scaleStartLabel: "0 - menor intensidad",
    scaleEndLabel: "63 - mayor intensidad",
    ranges: [
      { label: "0-21", min: 0, minInclusive: true, max: 21 },
      { label: "22-35", min: 22, minInclusive: true, max: 35 },
      { label: ">=36", min: 36, minInclusive: true }
    ]
  },
  {
    key: "bdi",
    shortTitle: "Depresión",
    title: "Inventario de Depresión de Beck (BDI)",
    description:
      "Cuestionario para identificar y medir la gravedad de síntomas típicos de la depresión en adultos y adolescentes. Permite diferenciar a un paciente con un trastorno depresivo mayor de aquel que simplemente evidencia un ánimo bajo.",
    scoreMax: 63,
    scaleStartLabel: "0 - mínima",
    scaleEndLabel: "63 - grave",
    ranges: [
      { label: "0-13", min: 0, minInclusive: true, max: 13 },
      { label: "14-19", min: 14, minInclusive: true, max: 19 },
      { label: "20-28", min: 20, minInclusive: true, max: 28 },
      { label: "29-63", min: 29, minInclusive: true, max: 63 }
    ]
  },
  {
    key: "psqi",
    shortTitle: "Sueño",
    title: "Índice de Calidad de Sueño de Pittsburgh",
    description:
      "Cuestionario que mide la calidad de sueño y sus alteraciones en el último mes. Evalúa calidad de sueño subjetiva, latencia de sueño, duración del dormir, eficiencia de sueño, alteraciones de sueño, uso de medicamentos para dormir y disfunción diurna.",
    scoreMax: 21,
    scaleStartLabel: "0 - óptimo",
    scaleEndLabel: "21 - alterado",
    ranges: [
      { label: "0-4", min: 0, minInclusive: true, max: 4 },
      { label: ">5", min: 5, minInclusive: false }
    ]
  },
  {
    key: "pss",
    shortTitle: "Estrés",
    title: "Escala de Estrés Percibido",
    description:
      "Escala diseñada para medir el grado en que las situaciones de la vida son evaluadas como estresantes. Los ítems evalúan el grado en que las personas encuentran su vida impredecible, incontrolable o sobrecargada, componentes centrales de la experiencia de estrés.",
    scoreMax: 40,
    scaleStartLabel: "0 - bajo",
    scaleEndLabel: "40 - elevado",
    ranges: [
      { label: "0-19", min: 0, minInclusive: true, max: 19 },
      { label: "20-25", min: 20, minInclusive: true, max: 25 },
      { label: ">25", min: 25, minInclusive: false }
    ]
  }
];

const CgsModule = ({ module, result, order }) => {
  const {
    key,
    title,
    description,
    ranges,
    scoreMax,
    scaleStartLabel,
    scaleEndLabel
  } = module;
  const labels = Array.isArray(result?.labels) ? result.labels : [];
  const total = toNumberOrNull(result?.total);
  const selectedIndex = getSelectedRangeIndex(total, ranges, result?.indexResult);
  const guidanceLevels = getModuleGuidanceLevels(key);
  const selectedGuidance = getModuleGuidanceLevel(key, total, selectedIndex);
  const currentLabel =
    selectedIndex >= 0
      ? labels[selectedIndex] || guidanceLevels[selectedIndex]?.level || "Sin etiqueta"
      : selectedGuidance?.level || "Sin clasificación";
  const tone = getSeverityTone(selectedIndex, ranges.length);
  const totalPercent =
    typeof total === "number" && typeof scoreMax === "number" && scoreMax > 0
      ? clampPercent((total / scoreMax) * 100)
      : 0;
  const meaningText =
    selectedGuidance?.meaning ||
    (typeof total === "number"
      ? `Tu puntuación actual es ${total} y se ubica en la categoría "${currentLabel}". Este resultado es orientativo y debe interpretarse junto con tu contexto personal.`
      : "No fue posible calcular una categoría con la información recibida. Revisa el resultado y vuelve a intentarlo.");
  const recommendations =
    selectedGuidance?.recommendations?.length > 0
      ? selectedGuidance.recommendations
      : MODULE_RECOMMENDATIONS[tone] || MODULE_RECOMMENDATIONS.neutral;

  return (
    <article id={`cgs-module-${key}`} className={`cgs-card tone-${tone}`}>
      <div className="cgs-card-bar" />

      <div className="cgs-card-head">
        <div className="cgs-card-num">{order}</div>

        <div className="cgs-card-head-info">
          <h2 className="cgs-card-title">{title}</h2>
          <p className="cgs-card-desc">{description}</p>
        </div>

        <div className="cgs-card-result-badge" aria-label={`Resultado de ${title}`}>
          <span className="cgs-badge-label">Total obtenido</span>
          <span className="cgs-badge-score">{total ?? "N/A"}</span>
          <span className="cgs-badge-level">{currentLabel}</span>
        </div>
      </div>

      <div className="cgs-gauge-section">
        <div className="cgs-gauge-label-row">
          <span>{scaleStartLabel}</span>
          <span>{scaleEndLabel}</span>
        </div>

        <div className="cgs-gauge-track" aria-hidden="true">
          <div className="cgs-gauge-fill" style={{ width: `${totalPercent}%` }} />
          {typeof total === "number" && (
            <div className="cgs-gauge-marker" style={{ left: `${totalPercent}%` }} />
          )}
        </div>

        <div className="cgs-gauge-range-list">
          {ranges.map((range, index) => {
            const rangeTone = getSeverityTone(index, ranges.length);
            const isSelected = selectedIndex === index;

            return (
              <span
                key={`${key}-gauge-${range.label}`}
                className={`cgs-gauge-range-pill${isSelected ? " is-selected" : ""}`}
                style={{
                  "--pill-tone-color": getToneColorVar(rangeTone),
                  "--pill-tone-bg": getToneSoftVar(rangeTone)
                }}
              >
                <strong>{range.label}</strong>
                <span>{labels[index] || guidanceLevels[index]?.level || "Sin etiqueta"}</span>
              </span>
            );
          })}
        </div>
      </div>

      <div className="cgs-table-section">
        <p className="cgs-section-eyebrow">Rangos de referencia</p>

        <div className="cgs-table-wrapper">
          <table className="cgs-table">
            <thead>
              <tr>
                <th>Puntuación total</th>
                <th>Indicación</th>
              </tr>
            </thead>
            <tbody>
              {ranges.map((range, index) => {
                const isSelected = selectedIndex === index;
                const rowTone = getSeverityTone(index, ranges.length);

                return (
                  <tr
                    key={`${title}-${range.label}`}
                    className={isSelected ? "is-selected" : ""}
                    style={{
                      "--row-highlight-color": getHighlightColor(index, ranges.length),
                      "--row-highlight-bg": getToneSoftVar(rowTone),
                      "--row-tone-color": getToneColorVar(rowTone)
                    }}
                  >
                    <td>
                      <span className="cgs-range-cell">
                        {isSelected && <span className="cgs-arrow">▶</span>}
                        {range.label}
                      </span>
                    </td>
                    <td>{labels[index] || guidanceLevels[index]?.level || "Sin etiqueta"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="cgs-info-grid">
        <section className="cgs-info-box cgs-info-box--meaning">
          <h3 className="cgs-info-title">
            <span className="cgs-info-dot" />
            Qué significa
          </h3>
          <p className="cgs-info-text">{meaningText}</p>
        </section>

        <section className="cgs-info-box cgs-info-box--next">
          <h3 className="cgs-info-title">
            <span className="cgs-info-dot" />
            Recomendación general
          </h3>
          <ul className="cgs-info-list">
            {recommendations.map((item) => (
              <li key={`${key}-${item}`}>{item}</li>
            ))}
          </ul>
        </section>
      </div>
    </article>
  );
};

const CgsResults = () => {
  const location = useLocation();
  const apiResults = location.state?.response?.results ?? {};
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [activeModuleKey, setActiveModuleKey] = useState(MODULES[0]?.key ?? "");

  const handleSummarySelect = (moduleKey) => {
    setActiveModuleKey(moduleKey);
    const element = document.getElementById(`cgs-module-${moduleKey}`);
    if (!element) return;

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    element.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start"
    });
  };

  const handleDownloadPdf = async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);

    try {
      const blobUrl = await createCgsPdfBlobUrl({
        results: apiResults,
        companyName: "Deli Life Wellness",
        logoUrl: "/logo512.png"
      });

      const pdfWindow = window.open(blobUrl, "_blank");
      if (!pdfWindow) {
        alert("No se pudo abrir la pestaña del PDF. Revisa el bloqueador de ventanas.");
      }
    } catch (err) {
      console.error("Error generando PDF de CGS:", err);
      alert("Ocurrió un error al generar el PDF.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="cgs-results-page">
      <section className="cgs-hero" aria-labelledby="cgs-results-title">
        <div className="cgs-hero-tag">Evaluación integral de bienestar emocional</div>
        <h1 id="cgs-results-title" className="cgs-hero-title">
          Tus resultados:
        </h1>
        <p className="cgs-hero-sub">
          Revisa tu resultado por dimensión y consulta los rangos de referencia de cada escala.
        </p>

        <button
          className="cgs-download-button"
          type="button"
          onClick={handleDownloadPdf}
          disabled={isGeneratingPdf}
        >
          {isGeneratingPdf ? "Generando PDF..." : "Descargar resultados"}
        </button>
      </section>

      <div className="cgs-summary-strip" aria-label="Resumen de resultados CGS">
        {MODULES.map((module) => {
          const result = apiResults[module.key] || {};
          const total = toNumberOrNull(result.total);
          const labels = Array.isArray(result.labels) ? result.labels : [];
          const selectedIndex = getSelectedRangeIndex(total, module.ranges, result.indexResult);
          const tone = getSeverityTone(selectedIndex, module.ranges.length);
          const guidanceLevels = getModuleGuidanceLevels(module.key);
          const selectedLabel =
            selectedIndex >= 0
              ? labels[selectedIndex] || guidanceLevels[selectedIndex]?.level || "Sin etiqueta"
              : "Sin datos";
          const isActive = activeModuleKey === module.key;

          return (
            <button
              key={module.key}
              type="button"
              className={`cgs-summary-item${isActive ? " is-active" : ""}`}
              style={{
                "--summary-tone-color": getToneColorVar(tone),
                "--summary-tone-bg": getToneSoftVar(tone)
              }}
              onClick={() => handleSummarySelect(module.key)}
              aria-pressed={isActive}
            >
              <span className="cgs-summary-scale">{module.shortTitle}</span>
              <span className="cgs-summary-score">{total ?? "N/A"}</span>
              <span className="cgs-summary-level">{selectedLabel}</span>
            </button>
          );
        })}
      </div>

      <main className="cgs-results-main">
        {MODULES.map((module, index) => (
          <CgsModule key={module.key} module={module} result={apiResults[module.key]} order={index + 1} />
        ))}
      </main>
    </div>
  );
};

export default CgsResults;
